/**
 * Doctor note templates for the admin note modal.
 *
 * The template strings are LEGAL TEXT, ported VERBATIM from the provider app
 * (instacured-frontend-mobile-provider-v3/src/screens/notes/_components/templates.ts).
 * DO NOT EDIT THE WORDING. If it changes, it must change in both places together —
 * a note issued from admin and one issued from the provider app must be identical.
 *
 * Admins may only issue the standard doctor note and its Amazon variant (Omer
 * 2026-08-26). Amazon is not a separate note type: it is the same DOCTOR_NOTE with
 * AMAZON in `companies`, which selects the HR-compliant wording below. Travel,
 * return-to-work and lab/imaging notes remain provider-only and are absent here.
 */

export const NOTE_TYPE = {
  DOCTOR_NOTE: "DOCTOR_NOTE",
};

export const NOTE_COMPANIES = {
  AMAZON: "AMAZON",
};

/** ISO or Date -> "M/D/YY", the format the note bodies use. */
export const toTemplateDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
};

/** ISO or Date -> "MM/DD/YYYY", used for the date of birth. */
export const toTemplateDob = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}/${dd}/${d.getFullYear()}`;
};

/**
 * Build the note body.
 * @param {Object} data - { patientName, dateOfBirth, startDate, endDate, returnDate }
 * @param {Boolean} isAmazon - use the Amazon HR-compliant variant
 * @returns {String}
 */
export const buildNoteDescription = (data = {}, isAmazon = false) => {
  const patientName = data.patientName || "";
  const dob = toTemplateDob(data.dateOfBirth);
  const startDate = toTemplateDate(data.startDate);
  const endDate = toTemplateDate(data.endDate);
  const returnDate = toTemplateDate(data.returnDate);

  if (isAmazon) {
    return `I am writing this letter to inform you that ${patientName} DOB ${dob} must take a leave from work due to a medical condition. InstaCured would caution any non health professional determining the severity of an illness as they are not medical professionals and could constitute practicing medicine without a license, which is a criminal offense. As such, I certify this Amazon employee has a serious health condition as defined in the Amazon Physician Form. Excuse the patient from work from ${startDate} to ${endDate}. The patient reports at the time of the visit they were physically in one of the states we operate. To verify this note and dates authenticity please visit instacured.com/verify and enter in the code at the bottom of the page. I expect the patient to return to work/school on ${returnDate} without restrictions. Amazon and other employers are reminded not to request specific medical details for absences, as this may violate HIPAA and federal privacy laws. Amazon has faced legal scrutiny under the FMLA and ADA for related practices. See Doe v. SEPTA and EEOC v. Amazon.com Inc., No. 2:22-cv-01446 for precedent. For any questions feel free to contact us at the email below.`;
  }

  return `I am writing this letter to inform you that ${patientName} DOB ${dob} must take a leave from work/school due to a medical condition. Excuse the patient from work/school from ${startDate} to ${endDate}. The patient reports at the time of the visit they were physically in one of the states we operate. To verify this note and dates authenticity please visit instacured.com/verify and enter in the code at the bottom of the page. I expect the patient to return to work/school on ${returnDate} without restrictions. For any questions feel free to contact us at the email below.`;
};

/**
 * The literal phrase a chosen diagnosis replaces in the note body. Both templates
 * above contain it. If the body has been hand-edited so the phrase is gone, the
 * replacement silently does nothing — same behaviour as the provider app.
 */
export const DIAGNOSIS_PLACEHOLDER = "a medical condition";

/** Apply the selected ICD-10 diagnosis to a note body. */
export const applyDiagnosis = (content, diagnosis) => {
  if (!diagnosis) return content;
  return content.replace(DIAGNOSIS_PLACEHOLDER, diagnosis);
};

/**
 * Quick-pick chips. `condition` must match a MEDICAL_CONDITIONS entry EXACTLY —
 * a chip whose value is not in the list silently selects nothing.
 * Ported from provider v3 src/lib/topSymptoms.ts.
 */
export const TOP_SYMPTOMS = [
  { label: "Viral Illness", condition: "B34.9 - Viral Illness" },
  { label: "Nausea & Vomiting", condition: "R11.2 - Nausea with Vomiting" },
  { label: "Diarrhea", condition: "R19.7 - Diarrhea" },
  { label: "Headache", condition: "R51.9 - Headache" },
  { label: "Fatigue", condition: "R53.83 - Fatigue" },
  { label: "Migraine", condition: "G43.909 - Migraine" },
  { label: "Abdominal Pain", condition: "R10.9 - Abdominal pain" },
];

/**
 * ICD-10 options for the diagnosis autocomplete. Ported verbatim from provider v3
 * src/screens/notes/_components/DiagnosisAutocomplete.tsx so both apps offer the
 * same codes. The selected value is the whole "CODE - Label" string.
 */
export const MEDICAL_CONDITIONS = [
  "M62.6 - Muscle strain",
  "M25.562 - Knee Pain",
  "M79.643 - Pain in Hand",
  "M25.519 - Pain in Shoulder",
  "M25.539 - Wrist Pain",
  "M79.1 - Myalgia",
  "N94.3 - Premenstrual Tension Syndrome",
  "R53.1 - Weakness",
  "R53.83 - Fatigue",
  "R50.9 - Fever",
  "B34.9 - Viral Illness",
  "N94.6 - Dysmenorrhea",
  "J01.90 - Acute Sinusitis",
  "J30.9 - Allergic Rhinitis",
  "K08.89 - Other specified disorders of teeth and supporting structures",
  "R09.81 - Nasal Congestion",
  "R07.0 - Sore Throat",
  "R09.81 - Rhinorrhea",
  "R06.02 - Shortness of Breath",
  "J45.901 - Asthma Exacerbation",
  "R05.9 - Cough",
  "A09 - Infectious Gastroenteritis",
  "R10.9 - Abdominal pain",
  "R19.7 - Diarrhea",
  "R11.0 - Nausea",
  "R11.2 - Nausea with Vomiting",
  "M54.5 - Low Back Pain",
  "M54.9 - Back Pain, Generalized",
  "M54.2 - Cervicalgia",
  "G43.909 - Migraine",
  "R51.9 - Headache",
  "H81.1 - Benign Paroxysmal Vertigo",
  "H10.9 - Conjunctivitis",
  "R42 - Dizziness",
  "F41.0 - Panic Disorder",
  "F41.9 - Anxiety",
  "F34.1 - Persistent Depressive Disorder",
  "Z73.3 - Stress, not elsewhere classified",
  "J06.9 - Acute upper respiratory infection",
  "J02.9 - Acute pharyngitis",
  "J20.9 - Acute bronchitis",
  "J03.90 - Acute tonsillitis",
  "N39.0 - Urinary tract infection",
  "L30.9 - Dermatitis",
  "L03.90 - Cellulitis",
  "K59.00 - Constipation",
  "H66.90 - Otitis media",
  "H60.90 - Otitis externa",
  "U07.1 - COVID-19",
  "K21.9 - Gastro-esophageal reflux disease",
  "L50.9 - Urticaria",
  "L23.9 - Allergic contact dermatitis",
  "G47.00 - Insomnia",
];

/** Case-insensitive substring match over the whole "CODE - Label" string. */
export const filterConditions = (query) => {
  const q = String(query || "").trim();
  if (!q) return MEDICAL_CONDITIONS;
  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(escaped, "i");
  return MEDICAL_CONDITIONS.filter((c) => re.test(c));
};
